import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AppContext } from '../../../context/context';
import { decryptData } from '../../../utils/utils';
import { ls } from '../../../localStorage';
import apis from '../../../apis/api';
import { skillsFromPlan, skillTitle } from './skillCatalogue';
import { Theme } from '../../adaptivePractice/theme';
import { FAClose } from '../../../components/atoms/fontawesomeIcon/Fontawesome';
import QuestionCard from './QuestionCard';
import QuizPrinter, { PrintedAnswer, PrintedQuestion } from './QuizPrinter';
import QuestionFrame from './QuestionFrame';
import SelectedQuestions from './SelectedQuestions';
import { useEngineAccess } from './useEngineAccess';
import SaveQuizDialog from './SaveQuizDialog';
import Toggle from './Toggle';
import {
  clampPoints,
  flattenUnitPlan,
  Module,
  PickedQuestion,
  pointsForBlooms,
  questionKey,
  questionLabel,
} from './types';
import './builder.css';

/** A question offered to the teacher: the rendered engine output plus what freezes it. */
interface OfferedQuestion {
  type: 'AdaptivePractice' | 'HOTS';
  question_id: string;
  lo_id?: string;
  review_obj?: any;
  blooms?: string;
  /** The engine's whole answer, which is what the play screen renders from - and what says, in
   *  `testJSON`, how the question reads. */
  response?: any;
  skillName?: string;
  /** View mode only: what the saved quiz says this question is worth. */
  points?: number;
}

/**
 * Tells the screen that asked for a download how far it has got. It draws nothing: the builder is
 * off-screen while it prepares a quiz, so the only way to say anything is to say it to the parent.
 */
const DownloadProgress = ({
  quizId,
  loading,
  questionCount,
  notice,
  report,
  startAt,
}: {
  quizId: string;
  loading: boolean;
  questionCount: number;
  notice: string;
  report: (status: string, detail: any) => void;
  /** Where a download picked up again, when an earlier opening drew the questions before it. */
  startAt: number;
}) => {
  useEffect(() => {
    // Picking up part way through: the teacher has already watched the first questions being
    // created, and is told where it carries on rather than that it is starting over.
    const resumed = startAt > 0 && !notice;
    const message = resumed
      ? `Creating question ${startAt + 1}${questionCount > 0 ? ` of ${questionCount}` : ''}…`
      : notice
      ? notice
      : loading
        ? 'Loading the questions…'
        : questionCount > 0
          ? `Preparing ${questionCount} question${questionCount === 1 ? '' : 's'}…`
          : 'Loading the quiz…';
    /**
     * A WORD IS NOT THE SAME AS A FAILURE.
     *
     * Only a quiz with no questions to draw has nothing to download; a quiz that could draw most
     * of them has something worth having, and the sheet is better with the questions it managed
     * than not at all. A notice that only says SOME questions could not be shown is not a failure.
     */
    report('adaptiveQuizDownloadStatus', {
      quiz_id: quizId,
      message,
      failed: Boolean(notice) && !loading && questionCount === 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, questionCount, notice]);
  return null;
};

/** Teacher-web's loading state, not the play screens' themed animation: this is a teacher tool that
 *  opens inside the portal, so it waits the way the portal waits. */
const PortalLoader = ({ label = 'Loading…' }: { label?: string }) => (
  <div className="aq-loading">
    <span className="aq-spinner" role="status" aria-label={label} />
    <p>{label}</p>
  </div>
);

/**
 * Teacher-web's two typefaces. The builder is shown inside that portal, so it borrows them rather
 * than looking like a different product - but only while this screen is open, and never blocking:
 * the stylesheet is appended once, and the screen renders on the fallback until it arrives.
 */
const loadPortalFonts = () => {
  const id = 'aq-portal-fonts';
  if (document.getElementById(id)) return;
  const preconnect = document.createElement('link');
  preconnect.rel = 'preconnect';
  preconnect.href = 'https://fonts.gstatic.com';
  preconnect.crossOrigin = 'anonymous';
  document.head.appendChild(preconnect);
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href =
    'https://fonts.googleapis.com/css2?family=Fredoka:wght@500;600;700&family=Poppins:wght@400;500;600;700;800;900&display=swap';
  document.head.appendChild(link);
};

/** The HOTS entry sits at the end of the skill list, not inside it - a HOTS belongs to the
 *  sub-topic's gaps rather than to one skill. */
const HOTS_OPTION = '__hots__';

/** A skill can hold dozens of question types. They are rendered a page at a time, as the old quiz
 *  builder did: every card is a live engine render, and a hundred at once is a slow screen. */
const PAGE_SIZE = 10;

/** Past this a quiz stops being a quiz, and the frozen questions make the saved body large. Picking
 *  is stopped here with a plain message rather than failing at save. */
const MAX_PICKS = 50;

/**
 * Adaptive quiz builder (teacher).
 *
 * The whole flow lives here: the teacher picks Module -> Sub-topic -> Skill, sees every question
 * that skill can ask, ticks the ones they want, sets what each is worth, and saves. The questions
 * come from the adaptive engine, and each pick is FROZEN - the numbers the teacher saw are stored
 * with it (review_obj), so every student gets the identical question.
 *
 * With `quizId` in the payload the same screen is read-only: the saved quiz's frozen questions,
 * replayed exactly as the students will get them.
 *
 * teacher-web opens this in an iframe and only ever hears `close` back: saving happens here.
 */
export function AdaptiveQuizBuilder() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const QUERY = queryParams.get('query');
  const lang = queryParams.get('lang') || 'en';
  const state: any = useContext(AppContext);

  const [launchError, setLaunchError] = useState('');
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [classroomId, setClassroomId] = useState('');
  const [curriculumId, setCurriculumId] = useState('');
  const [grade, setGrade] = useState<number | undefined>(undefined);
  const [isAuto, setIsAuto] = useState(false);
  const [quizId, setQuizId] = useState('');
  /** Editing a saved quiz rather than reading it: the same questions, back in the builder. */
  const [isEdit, setIsEdit] = useState(false);
  /** Opened only to put the quiz on paper: nothing is shown, the questions go back to teacher-web,
   *  which owns the DOC and the PDF. */
  const [isDownload, setIsDownload] = useState(false);

  const [modules, setModules] = useState<Module[]>([]);
  /** The plan as the service sent it. Auto quiz hands it to the engine, which reads the whole thing
   *  to decide HOTS eligibility - the flattened version above has only what the pickers need. */
  const [planRaw, setPlanRaw] = useState<any>(null);
  const [moduleId, setModuleId] = useState('');
  const [subTopicId, setSubTopicId] = useState('');
  const [skillId, setSkillId] = useState('');
  /** Only the sub-topic's skills the ENGINE has. A lo_id the bank does not know is left out of
   *  the dropdown rather than offered and failing when its questions load. */
  const [skills, setSkills] = useState<{ loId: string; name: string }[]>([]);


  /** Auto quiz: the sub-topics to draw from, how many questions, and whether HOTS may be included. */
  const [autoSubTopicIds, setAutoSubTopicIds] = useState<string[]>([]);
  /**
   * How many questions to build, AS TYPED - a string, so the box can be empty while they edit it.
   *
   * It used to hold a number and force every keystroke into 1..MAX_PICKS. That made the box
   * impossible to clear: backspacing to nothing gave 0, which came back as 1, so a teacher who
   * wanted 20 was left with a 1 they could not delete, and typing 20 after it gave 120 - capped, and
   * shown as 50. Nothing is corrected while they type now; Create says when the number is not one we
   * can use, which is where a teacher looks for why they cannot go on.
   */
  const [autoMax, setAutoMax] = useState('10');
  const [autoHots, setAutoHots] = useState(false);
  /** The number behind the box, and whether a quiz can be built from it. */
  const autoMaxValue = Number(autoMax);
  const autoMaxIsValid =
    /^\d+$/.test(autoMax.trim()) && autoMaxValue >= 1 && autoMaxValue <= MAX_PICKS;
  const [autoHasHots, setAutoHasHots] = useState(false);
  const [generating, setGenerating] = useState(false);
  /** Every question showing its worked explanation, for a teacher who needs the answers. */
  const [showAnswers, setShowAnswers] = useState(false);
  /** The Selected panel taking most of the room, with the questions in a single column. */
  const [widePanel, setWidePanel] = useState(false);
  const [hasHots, setHasHots] = useState(false);

  const [offered, setOffered] = useState<OfferedQuestion[]>([]);
  const [page, setPage] = useState(0);
  const [picked, setPicked] = useState<PickedQuestion[]>([]);
  /** Ticked, not yet taken: a question joins the quiz when the teacher moves it. */
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  /** What a question is worth before it is moved. After the move the Selected list owns it. */
  const [refreshing, setRefreshing] = useState('');
  /** Which question fills the window, by its key. Empty means the list is showing. */
  const [expandedKey, setExpandedKey] = useState('');
  const [loadingPlan, setLoadingPlan] = useState(true);
  const [loadingSkills, setLoadingSkills] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [notice, setNotice] = useState('');
  const [saveOpen, setSaveOpen] = useState(false);
  /** Asking before the quiz is emptied - the one thing in this screen that cannot be undone by
   *  pressing the same button again. */
  const [clearOpen, setClearOpen] = useState(false);

  /** View mode: the saved quiz, rendered read-only. */
  const [viewQuiz, setViewQuiz] = useState<any>(null);

  // Every call into the engine goes through this: one at a time, newest wins, nothing lands after
  // the screen closes. See the hook for why each of those matters.
  const { engine, run: runOnEngine, claim, holds, abandon, alive } = useEngineAccess(Theme.Dawn);
  useEffect(() => loadPortalFonts(), []);

  useEffect(() => {
    if (!expandedKey) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setExpandedKey('');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expandedKey]);

  const langObj = useMemo(() => {
    // Same chain as the play screens: the classroom's country wins over the teacher's own.
    const country = String(
      currentUser?.classroom_country_code || currentUser?.country_code || 'in',
    ).toLowerCase();
    return { lang: `${lang}_${country}`, number_lang: 'en' };
  }, [lang, currentUser]);

  const launch = useMemo(() => {
    try {
      return QUERY ? decryptData(QUERY) : null;
    } catch {
      return null;
    }
  }, [QUERY]);

  const close = useCallback(() => state?.messageForParent?.('close'), [state]);

  useEffect(() => {
    try {
      if (!launch) throw new Error('Missing or unreadable query');
      const user = launch.currentUser;
      if (!user?.token || !user?.dbToken) throw new Error('Invalid session');
      const viewing = String(launch.quizId || '');
      const classroom = String(launch.classroom_id || user.classroom_id || '');
      const curriculum = String(launch.curriculum_id || '');
      const gradeNumber = Number(launch.grade);
      // Creating needs all three: the save API requires them, and a teacher should be told now
      // rather than after picking twenty questions.
      if (!viewing && (!classroom || !curriculum || !Number.isFinite(gradeNumber) || gradeNumber <= 0)) {
        throw new Error('Missing classroom, curriculum or grade');
      }
      ls.setToken(user.token);
      ls.setDBToken(user.dbToken);
      setCurrentUser(user);
      setClassroomId(classroom);
      setCurriculumId(curriculum);
      setGrade(Number.isFinite(gradeNumber) && gradeNumber > 0 ? gradeNumber : undefined);
      setIsAuto(launch.isAuto === true);
      setQuizId(viewing);
      setIsEdit(viewing ? launch.isEdit === true : false);
      setIsDownload(viewing ? launch.isDownload === true : false);
    } catch (error) {
      console.log('quiz builder: could not read launch payload', error);
      setLaunchError('This screen could not be opened. Please close and try again.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** The classroom's unit plan drives every picker - the same plan teacher-web reads. Not needed
   *  when viewing a saved quiz: that one carries its own questions. */
  useEffect(() => {
    if (!currentUser) return;
    // Reading a saved quiz needs no plan: there is nothing to pick. Building one does - and so does
    // EDITING one, where the teacher adds questions, and auto, which sends the plan to the engine.
    if (quizId && !isEdit) {
      setLoadingPlan(false);
      return;
    }
    if (!classroomId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingPlan(true);
        const res: any = await apis._getUnitPlanForTeacher(classroomId);
        const flat = flattenUnitPlan(res?.data?.collection);
        if (cancelled) return;
        setPlanRaw(res?.data?.collection || null);
        setModules(flat);
        if (flat.length === 0) setNotice('This classroom has no skills switched on yet.');
      } catch (error) {
        console.log('quiz builder: could not load the unit plan', error);
        if (!cancelled) setNotice('Could not load this classroom’s modules. Please close and try again.');
      } finally {
        if (!cancelled) setLoadingPlan(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [classroomId, currentUser, isEdit, quizId]);

  /** View mode: load the quiz and replay each frozen question exactly as a student will get it. */
  useEffect(() => {
    if (!quizId || !currentUser) return;
    let cancelled = false;
    (async () => {
      setLoadingQuestions(true);
      try {
        const res: any = await apis._viewAdaptiveQuiz(quizId);
        const quiz = res?.data?.collection;
        if (!quiz) throw new Error(res?.data?.message || 'Quiz not found');
        if (cancelled) return;
        setViewQuiz(quiz);

        const rendered: OfferedQuestion[] = [];
        for (const question of quiz.questions || []) {
          try {
            // The skill name is read INSIDE the queued job: it comes from whatever skill the
            // engine has loaded at that moment, and outside the queue that is no longer this one.
            const rendering: any = await runOnEngine(async () => {
              if (question.type === 'HOTS') {
                engine.content.setTheme(Theme.Dawn);
                const loaded: any = engine.content.loadHotsQuestionById(question.question_id);
                if (!loaded) return null;
                const hots = await engine.factory.displayQuestion(
                  engine.content,
                  engine.content.createContentTemplateObject(loaded.id, 'REACT_WEB'),
                  langObj,
                );
                return { response: hots, skillName: 'HOTS' };
              }
              await engine.content.loadSkillBySkillId(String(question.lo_id), Theme.Dawn, langObj.lang);
              const skill = await engine.factory.displayQuestion(
                engine.content,
                engine.content.createContentTemplateObject(question.question_id, 'REACT_WEB'),
                langObj,
                // The stored freeze: same numbers, same option order the teacher saved.
                question.review_obj,
              );
              return {
                response: skill,
                // The title a teacher reads, not the engine's file name for the same skill.
                skillName:
                  skillTitle(String(question.lo_id)) ||
                  engine.content.getSkillName() ||
                  '',
              };
            });
            const response: any = rendering?.response;
            if (!response?.component) continue;
            rendered.push({
              type: question.type,
              question_id: question.question_id,
              lo_id: question.lo_id ? String(question.lo_id) : undefined,
              review_obj: question.review_obj,
              blooms: response.blooms,
              response,
              skillName: rendering.skillName,
              points: Number(question.points) || 1,
            });
          } catch (error) {
            // One unreadable question must not hide the rest of the quiz.
            console.log('quiz builder: could not replay a saved question', error);
          }
        }
        if (cancelled) return;
        if (launch?.isEdit === true && launch?.isDownload !== true) {
          // Editing: they are the quiz so far, not a list to choose from. The teacher adds to them
          // and takes them away in the Selected panel, and the skills pickers start empty as usual.
          setPicked(
            rendered.map((q) => ({
              type: q.type,
              question_id: q.question_id,
              lo_id: q.lo_id,
              review_obj: q.review_obj,
              blooms: q.blooms,
              skillName: q.skillName,
              label: questionLabel(q.response, q.skillName || q.type),
              response: q.response,
              points: q.points ?? 1,
            })),
          );
        } else {
          setOffered(rendered);
        }
        if (rendered.length === 0) setNotice('This quiz’s questions could not be shown.');
        else if (rendered.length < (quiz.questions || []).length) {
          setNotice(
            `${(quiz.questions || []).length - rendered.length} of this quiz's questions could not be shown here. Students still get all of them.`,
          );
        }
      } catch (error) {
        console.log('quiz builder: could not load the quiz', error);
        if (!cancelled) setNotice('Could not load this quiz. Please close and try again.');
      } finally {
        if (!cancelled) setLoadingQuestions(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser, engine, langObj, quizId, runOnEngine]);

  const currentModule = modules.find((m) => m.id === moduleId);
  const currentSubTopic = currentModule?.subTopics.find((s) => s.id === subTopicId);

  /**
   * The skill list for a sub-topic, from the engine's own catalogue of what it can serve.
   *
   * A plan carries objectives that adaptive practice has never covered; those are left out, because
   * offering one would fail the moment a teacher picked it. The catalogue answers both questions at
   * once - whether the engine has the skill, and what it is called - without loading anything.
   *
   * HOTS eligibility is asked once per sub-topic, exactly as the dashboard card asks it.
   */
  useEffect(() => {
    if (!currentSubTopic) {
      setSkills([]);
      setHasHots(false);
      return;
    }
    let cancelled = false;
    const loIds = currentSubTopic.loIds;
    (async () => {
      setLoadingSkills(true);
      setSkills([]);
      setHasHots(false);
      try {
        const available = skillsFromPlan(loIds);
        if (cancelled) return;
        setSkills(available);
        if (available.length === 0) setNotice('No questions for this sub-topic yet.');
        else setNotice('');
        try {
          const eligible = await runOnEngine(async () => engine.content.hasHots(loIds, grade));
          if (!cancelled) setHasHots(Boolean(eligible));
        } catch {
          if (!cancelled) setHasHots(false);
        }
      } finally {
        if (!cancelled) setLoadingSkills(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentSubTopic, engine, grade, langObj.lang, runOnEngine]);

  /** Render every question a skill can ask, once each, read-only. */
  const loadSkillQuestions = useCallback(
    async (loId: string) => {
      const ticket = claim();
      setLoadingQuestions(true);
      setNotice('');
      setOffered([]);
      setPage(0);
      try {
        const questions = await runOnEngine(async () => {
          await engine.content.loadSkillBySkillId(loId, Theme.Dawn, langObj.lang);
          const skillName = skillTitle(loId) || engine.content.getSkillName() || '';
          const count = Number(engine.content.getNumberOfQuestions()) || 0;
          const found: OfferedQuestion[] = [];
          for (let n = 0; n < count; n += 1) {
            if (!holds(ticket)) break;
            const loaded: any = engine.content.loadQuestionByNumber(n);
            if (!loaded?.questionId) continue;
            try {
              const response: any = await engine.factory.displayQuestion(
                engine.content,
                engine.content.createContentTemplateObject(loaded.questionId, 'REACT_WEB'),
                langObj,
              );
              // A skill question is only offerable if the engine gave back the freeze with it:
              // without review_obj the quiz could not replay the numbers the teacher saw, and the
              // save API rejects it. Better never offered than rejected after being picked.
              if (!response?.component || !response?.reviewObj) continue;
              found.push({
                type: 'AdaptivePractice',
                question_id: loaded.questionId,
                lo_id: loId,
                review_obj: response.reviewObj,
                blooms: response.blooms,
                response,
                skillName,
              });
            } catch (error) {
              // A single question type that cannot render is skipped, not fatal for the skill.
              console.log('quiz builder: skipping an unrenderable question', loaded.questionId, error);
            }
          }
          return found;
        });
        if (!holds(ticket)) return;
        setOffered(questions);
        if (questions.length === 0) setNotice('This skill has no questions to show.');
      } catch (error) {
        console.log('quiz builder: could not load the skill', error);
        if (!holds(ticket)) return;
        setNotice('Could not load this skill’s questions.');
        setOffered([]);
      } finally {
        if (holds(ticket)) setLoadingQuestions(false);
      }
    },
    [claim, engine, holds, langObj, runOnEngine],
  );

  /** The sub-topic's HOTS, the same set the teacher HOTS screen shows. */
  const loadHotsQuestions = useCallback(async () => {
    if (!currentSubTopic) return;
    const loIds = currentSubTopic.loIds;
    const ticket = claim();
    setLoadingQuestions(true);
    setNotice('');
    setOffered([]);
    setPage(0);
    try {
      const userObj = {
        userId: currentUser.id,
        classroomId,
        dbToken: ls.getDBToken(),
        token: ls.getToken(),
        env: process.env.REACT_APP_ENV || '',
      };
      const questions = await runOnEngine(async () => {
        const { serve }: any = await engine.content.getHotsForTeacher(userObj, loIds, grade);
        const found: OfferedQuestion[] = [];
        for (const item of serve || []) {
          if (!holds(ticket)) break;
          // loadHotsQuestionById takes no theme, so it is stamped here or the engine keeps
          // whatever the last screen left behind.
          engine.content.setTheme(Theme.Dawn);
          const loaded: any = engine.content.loadHotsQuestionById(item.id);
          if (!loaded) continue;
          try {
            const response: any = await engine.factory.displayQuestion(
              engine.content,
              engine.content.createContentTemplateObject(loaded.id, 'REACT_WEB'),
              langObj,
            );
            if (!response?.component) continue;
            found.push({
              type: 'HOTS',
              question_id: loaded.id,
              blooms: response.blooms || 'HOTS',
              response,
              // A HOTS is replayed by its id, so the quiz does not NEED its review object - but it
              // is kept anyway, so a HOTS row in adaptive_questions carries the answer key exactly
              // as a HOTS answered in practice does, and reports read one shape.
              review_obj: response.reviewObj,
              // Which sub-topic it was taken from, said in the name. A HOTS belongs to no single
              // skill - it draws on the whole sub-topic - so on its own it would read as "HOTS" for
              // every one of them, and a quiz that draws HOTS from two sub-topics would show the
              // same word twice with no way to tell which is which.
              skillName: currentSubTopic?.name
                ? `HOTS · ${currentSubTopic.name}`
                : 'HOTS',
            });
          } catch (error) {
            console.log('quiz builder: skipping an unrenderable HOTS', item.id, error);
          }
        }
        return found;
      });
      if (!holds(ticket)) return;
      setOffered(questions);
      if (questions.length === 0) setNotice('No HOTS for this sub-topic yet.');
    } catch (error) {
      console.log('quiz builder: could not load HOTS', error);
      if (!holds(ticket)) return;
      setNotice('Could not load HOTS for this sub-topic.');
      setOffered([]);
    } finally {
      if (holds(ticket)) setLoadingQuestions(false);
    }
  }, [claim, classroomId, currentSubTopic, currentUser, engine, grade, holds, langObj, runOnEngine]);

  const onSelectSkill = (value: string) => {
    setSkillId(value);
    if (!value) {
      // Abandon whatever is in flight: nothing is selected, so nothing should land.
      abandon();
      setOffered([]);
      setPage(0);
      setLoadingQuestions(false);
      setNotice('');
      return;
    }
    if (value === HOTS_OPTION) void loadHotsQuestions();
    else void loadSkillQuestions(value);
  };

  /**
   * Auto quiz: the engine picks the questions, the teacher keeps or drops them.
   *
   * It is handed every skill of the chosen sub-topics and a cap, and hands back the questions with
   * the numbers already frozen. Each one is then drawn here exactly as a student will get it - the
   * same replay a saved quiz gets - so what the teacher looks at is what the class will see, and
   * they land in the Selected panel where points can be changed and any of them thrown out.
   */
  const generateQuiz = useCallback(async () => {
    const chosen = modules
      .flatMap((m) => m.subTopics)
      .filter((t) => autoSubTopicIds.indexOf(t.id) !== -1);
    const loIds = Array.from(new Set(chosen.flatMap((t) => t.loIds)));
    if (loIds.length === 0) {
      setNotice('Choose at least one sub-topic.');
      return;
    }
    if (!autoMaxIsValid) {
      setNotice(`Say how many questions you want, from 1 to ${MAX_PICKS}.`);
      return;
    }

    const ticket = claim();
    setGenerating(true);
    setNotice('');
    try {
      const userObj = {
        userId: currentUser.id,
        classroomId,
        dbToken: ls.getDBToken(),
        token: ls.getToken(),
        env: process.env.REACT_APP_ENV || '',
      };
      const built: any = await runOnEngine(async () =>
        engine.content.generateQuiz(userObj as any, {
          loIds,
          maxQuestions: autoMaxValue,
          // HOTS are gated on the grade and drawn from the whole plan, so the engine reads the plan
          // itself rather than being told which ones qualify.
          ...(autoHots && planRaw && grade
            ? { hots: { grade: Number(grade), unitPlan: planRaw } }
            : {}),
        }),
      );
      const wanted: any[] = built?.questions || [];

      const drawn: PickedQuestion[] = [];
      for (const question of wanted) {
        if (!holds(ticket)) return;
        try {
          const rendering: any = await runOnEngine(async () => {
            if (question.type === 'HOTS') {
              engine.content.setTheme(Theme.Dawn);
              const loaded: any = engine.content.loadHotsQuestionById(question.question_id);
              if (!loaded) return null;
              const hots = await engine.factory.displayQuestion(
                engine.content,
                engine.content.createContentTemplateObject(loaded.id, 'REACT_WEB'),
                langObj,
                question.review_obj,
              );
              return { response: hots, skillName: 'HOTS' };
            }
            await engine.content.loadSkillBySkillId(String(question.lo_id), Theme.Dawn, langObj.lang);
            const skill = await engine.factory.displayQuestion(
              engine.content,
              engine.content.createContentTemplateObject(question.question_id, 'REACT_WEB'),
              langObj,
              question.review_obj,
            );
            return {
              response: skill,
              skillName:
                skillTitle(String(question.lo_id)) ||
                engine.content.getSkillName() ||
                '',
            };
          });
          const response: any = rendering?.response;
          // A question that cannot be drawn cannot be saved either - the create API needs its
          // frozen numbers - so it is left out rather than offered and failing later.
          if (!response?.component || !(question.review_obj || response.reviewObj)) continue;
          // The same question once per skill, and a HOTS once altogether - the save is refused
          // otherwise, because a student's attempt could not be stored twice under one key. The same
          // question under a DIFFERENT skill is fine, which is why this keys on both.
          const key = questionKey({
            type: question.type,
            lo_id: question.lo_id ? String(question.lo_id) : undefined,
            question_id: question.question_id,
          });
          if (drawn.some((d) => questionKey(d) === key)) continue;
          drawn.push({
            type: question.type,
            question_id: question.question_id,
            lo_id: question.lo_id ? String(question.lo_id) : undefined,
            review_obj: question.review_obj || response.reviewObj,
            blooms: question.blooms || response.blooms,
            skillName: rendering.skillName,
            label: questionLabel(response, rendering.skillName || question.type),
            response,
            points: pointsForBlooms(question.blooms || response.blooms),
          });
        } catch (error) {
          console.log('quiz builder: skipping a generated question', question?.question_id, error);
        }
      }

      if (!holds(ticket)) return;
      setPicked(drawn);
      if (drawn.length === 0) {
        setNotice('No questions could be built for these sub-topics. Try another one.');
      } else if (drawn.length < autoMaxValue) {
        setNotice(
          `Only ${drawn.length} question${drawn.length === 1 ? '' : 's'} available for these sub-topics.`,
        );
      }
    } catch (error) {
      console.log('quiz builder: could not build an auto quiz', error);
      if (holds(ticket)) setNotice('Could not build the quiz. Please try again.');
    } finally {
      if (holds(ticket)) setGenerating(false);
    }
  }, [
    autoHots,
    autoMaxIsValid,
    autoMaxValue,
    autoSubTopicIds,
    claim,
    classroomId,
    currentUser,
    engine,
    grade,
    holds,
    langObj,
    modules,
    planRaw,
    runOnEngine,
  ]);

  /** Whether the chosen sub-topics have any HOTS for this grade - the same question the skill list
   *  asks before it offers HOTS at all. Asked again whenever the choice changes. */
  useEffect(() => {
    if (!isAuto || autoSubTopicIds.length === 0) {
      setAutoHasHots(false);
      setAutoHots(false);
      return;
    }
    let cancelled = false;
    const loIds = Array.from(
      new Set(
        modules
          .flatMap((m) => m.subTopics)
          .filter((t) => autoSubTopicIds.indexOf(t.id) !== -1)
          .flatMap((t) => t.loIds),
      ),
    );
    (async () => {
      try {
        const eligible = await runOnEngine(async () =>
          engine.content.hasHots(loIds, grade),
        );
        if (!cancelled) setAutoHasHots(Boolean(eligible));
      } catch {
        if (!cancelled) setAutoHasHots(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [autoSubTopicIds, engine, grade, isAuto, modules, runOnEngine]);

  /** Draw one offered question again: render it WITHOUT its review_obj, so the engine picks fresh
   *  content for it - new numbers in most, a different picture or arrangement in the rest. */
  const refreshQuestion = async (question: OfferedQuestion) => {
    if (question.type !== 'AdaptivePractice' || !question.lo_id) return;
    const key = questionKey(question);
    setRefreshing(key);
    try {
      const response: any = await runOnEngine(async () => {
        await engine.content.loadSkillBySkillId(String(question.lo_id), Theme.Dawn, langObj.lang);
        return engine.factory.displayQuestion(
          engine.content,
          engine.content.createContentTemplateObject(question.question_id, 'REACT_WEB'),
          langObj,
        );
      });
      // No freeze back means the new numbers could not be stored: keep the question the teacher
      // already has rather than replacing it with one the save API would reject.
      if (!alive.current || !response?.component || !response?.reviewObj) return;
      // Match on the list that is on screen NOW: the teacher may have changed skill meanwhile, in
      // which case this question is no longer shown and only the pick needs the new numbers.
      setOffered((current) =>
        current.map((q) =>
          questionKey(q) === key
            ? {
                ...q,
                review_obj: response.reviewObj,
                blooms: response.blooms,
                response,
              }
            : q,
        ),
      );
      // A picked question follows what is on screen: the teacher keeps what they can see.
      setPicked((current) =>
        current.map((p) =>
          questionKey(p) === key
            ? {
                ...p,
                review_obj: response.reviewObj,
                blooms: response.blooms,
                response,
                label: questionLabel(response, p.skillName || p.type),
              }
            : p,
        ),
      );
    } catch (error) {
      console.log('quiz builder: could not refresh the question', error);
    } finally {
      setRefreshing((current) => (current === key ? '' : current));
    }
  };

  /** Picks are held by skill + question, never by question id alone: two skills can both name a
   *  question "q1", and keying on the id would silently replace one with the other. */
  const pickedKeys = useMemo(() => picked.map((p) => questionKey(p)), [picked]);

  /** What is still on offer: a question that has been moved into the quiz leaves this list, as it
   *  did in the old builder. Delete it from the Selected list and it comes back here. */
  const available = useMemo(
    () => offered.filter((q) => pickedKeys.indexOf(questionKey(q)) === -1),
    [offered, pickedKeys],
  );

  const availableKeys = useMemo(() => available.map((q) => questionKey(q)), [available]);

  /**
   * The Selected panel takes the room when it HAS something and the question area has nothing.
   *
   * That covers both ways of building. Auto never fills the question area at all - its questions are
   * drawn straight into Selected - so the panel spreads out as soon as they are generated, and not
   * before, when there would be nothing in it to see. Manual fills that area and empties it again
   * once every question has been moved across, and then the panel is all that is left to look at.
   * Either way the room goes back the moment questions appear: a new skill, or a fresh generation.
   *
   * It only reacts to those two counts, so a teacher who sets the panel the other way keeps it until
   * one of them changes.
   */
  useEffect(() => {
    setWidePanel(available.length === 0 && picked.length > 0);
  }, [available.length, picked.length]);

  /** What a question is worth before it is moved: its Bloom's default. It becomes editable in the
   *  Selected panel, which is where the quiz is actually put together. */
  const pointsFor = (question: OfferedQuestion) => pointsForBlooms(question.blooms);

  const isSelected = (question: OfferedQuestion) => selectedKeys.indexOf(questionKey(question)) !== -1;

  const toggleSelected = (question: OfferedQuestion) => {
    const key = questionKey(question);
    setSelectedKeys((current) =>
      current.indexOf(key) === -1 ? [...current, key] : current.filter((k) => k !== key),
    );
  };

  /** Put a question somewhere else in the quiz: students are asked them in this order. */
  const reorderPicked = (from: number, to: number) =>
    setPicked((current) => {
      if (from === to || from < 0 || to < 0) return current;
      if (from >= current.length || to >= current.length) return current;
      const next = [...current];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });

  /** Move the ticked questions into the quiz. */
  const moveSelected = () => {
    const moving = available.filter((q) => isSelected(q));
    if (moving.length === 0) return;
    const room = MAX_PICKS - picked.length;
    const taken = moving.slice(0, Math.max(0, room));
    setPicked((current) => [
      ...current,
      ...taken.map((question) => ({
        type: question.type,
        question_id: question.question_id,
        lo_id: question.lo_id,
        review_obj: question.review_obj,
        blooms: question.blooms,
        skillName: question.skillName,
        label: questionLabel(question.response, question.skillName || question.type),
        response: question.response,
        points: pointsFor(question),
      })),
    ]);
    const movedKeys = taken.map((q) => questionKey(q));
    setSelectedKeys((current) => current.filter((key) => movedKeys.indexOf(key) === -1));
    // No message here any more: the quiz being full is said by quizIsFull, which is on screen
    // BEFORE they pick something they cannot have rather than after.
    setNotice('');
  };

  /**
   * The quiz is full. Said as soon as it IS full, rather than when a teacher tries to add the
   * fifty-first and is turned away - by then they have already chosen a question they cannot have.
   *
   * Derived, not remembered: remove a question and the message goes, because adding is possible
   * again. That also means there is nothing to clear when the teacher moves to another skill.
   */
  const quizIsFull = picked.length >= MAX_PICKS;

  /** Points, changed from the Selected list - the only place a moved question can be edited. */
  const setPickedPoints = (key: string, points: number) =>
    setPicked((current) =>
      current.map((p) => (questionKey(p) === key ? { ...p, points: clampPoints(points) } : p)),
    );

  const removePicked = (key: string) =>
    setPicked((current) => current.filter((p) => questionKey(p) !== key));

  const pageQuestions = useMemo(
    () => available.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE),
    [available, page],
  );
  const pageCount = Math.max(1, Math.ceil(available.length / PAGE_SIZE));

  /** Moving every question on the last page leaves the teacher standing on a page that no longer
   *  exists - which looks exactly like the blank above. Step back to the last one there is. */
  useEffect(() => {
    if (page > 0 && page >= pageCount) setPage(pageCount - 1);
  }, [page, pageCount]);

  const allOnPageChecked = pageQuestions.length > 0 && pageQuestions.every((q) => isSelected(q));

  /** Tick or clear every question on this page - what the teacher can see. */
  const toggleAllOnPage = () => {
    const keys = pageQuestions.map((q) => questionKey(q));
    setSelectedKeys((current) =>
      allOnPageChecked
        ? current.filter((key) => keys.indexOf(key) === -1)
        : [...current, ...keys.filter((key) => current.indexOf(key) === -1)],
    );
  };

  const selectedOnPage = pageQuestions.filter((q) => isSelected(q)).length;

  /**
   * Why there is nothing to choose from, when there is nothing to choose from.
   *
   * A question moved into the quiz leaves this list, so taking every one of a skill's questions
   * empties the middle of the screen - the teacher has done nothing wrong, and has to be told that
   * rather than left looking at a blank column.
   */
  const emptyMessage = useMemo(() => {
    /**
      * No unit plan on this classroom. The notice above has already said there are no skills
      * switched on, and "choose a module" underneath it asks for something that does not exist.
      */
    if (modules.length === 0) return '';
    if (!subTopicId) return 'Choose a module and a sub-topic to start.';
    /**
     * Nothing to pick from. A sub-topic with no skills at all already says so in the notice above -
     * "No questions for this sub-topic yet" - and telling a teacher to pick a skill underneath that
     * contradicts it, since there are none to pick. Say nothing and let the notice stand.
     *
     * This also covers the moment the skills are still loading, where the list is empty but not
     * yet known to be empty: better a blank for an instant than an instruction to do something
     * impossible.
     */
    if (!skillId) return skills.length === 0 ? '' : 'Pick a skill to see its questions.';
    // No questions came back at all: the loader has already said so.
    if (offered.length === 0) return '';
    const name = skills.find((s) => s.loId === skillId)?.name;
    if (skillId === HOTS_OPTION) {
      return 'Every HOTS question from this sub-topic is now in the quiz. Choose another sub-topic to add more.';
    }
    return `Every question from ${name || 'this skill'} is now in the quiz. Choose another skill to add more.`;
  }, [modules.length, offered.length, skillId, skills, subTopicId]);

  const totalPoints = picked.reduce((sum, p) => sum + p.points, 0);
  // A saved quiz opened to be read; editing one puts it back in the builder instead.
  const readOnly = Boolean(quizId) && !isEdit;
  // Read back from the lists, never held separately: refreshing replaces the question there, and
  // what fills the window must be the new numbers rather than the ones it opened with.
  const expandedQuestion = expandedKey
    ? offered.find((q) => questionKey(q) === expandedKey) ||
      (picked.find((p) => questionKey(p) === expandedKey) as any)
    : undefined;

  // Putting the quiz on paper: the questions are rendered at page width and handed to teacher-web
  // as HTML. The teacher sees only that it is being prepared.
  //
  // BEFORE the landscape check below, and not by accident: a page is taller than it is wide, so the
  // window this renders in is too, and that check would take it for a phone held upright and put a
  // "turn your phone" notice where the quiz should be. Nobody is holding this window.
  if (isDownload) {
    return (
      <div className="aq-shell aq-centered">
        <PortalLoader label="Preparing the download…" />
        {notice ? <p className="aq-error">{notice}</p> : null}
        {/* This screen is off-screen - whoever asked for the download cannot see it, so it says
            where it has got to rather than leaving them watching a spinner. */}
        <DownloadProgress
          quizId={quizId}
          loading={loadingQuestions}
          questionCount={offered.length}
          notice={notice}
          report={(status: string, detail: any) => state?.messageForParent?.(status, detail)}
          startAt={Number(launch?.downloadFrom) || 0}
        />
        <QuizPrinter
          questions={offered}
          startAt={Number(launch?.downloadFrom) || 0}
          // Posted straight out rather than held in state: a re-render here would redraw the very
          // questions being drawn, half way through drawing them.
          onProgress={(drawn: number, total: number) =>
            state?.messageForParent?.('adaptiveQuizDownloadStatus', {
              quiz_id: quizId,
              message: `Creating question ${drawn} of ${total}…`,
            })
          }
          // A BATCH AT A TIME, each in its own message. A quiz of fifty handed over in one go is
          // sixty megabytes of pictures in a single string, which is a tab out of memory rather
          // than a download. Every message says whether it is the last, and the parent collects
          // them; the quiz's own details ride along with each so no message depends on another.
          onBatch={(
            printed: PrintedQuestion[],
            answers: PrintedAnswer[],
            isLast: boolean,
            nextFrom?: number,
          ) => {
            console.log('[adaptive-quiz] posting', printed.length, 'questions and', answers.length,
              'answers to the parent', isLast ? '(last batch)' : '');
            state?.messageForParent?.('adaptiveQuizForDownload', {
              quiz_id: quizId,
              name: viewQuiz?.name || 'Quiz',
              total_points: viewQuiz?.total_points ?? 0,
              questions: printed,
              answers,
              isLast,
              ...(nextFrom !== undefined ? { nextFrom } : {}),
            });
          }}
        />
      </div>
    );
  }

  // The play screen only runs in landscape, and it says so itself - once per frame, which on a page
  // of cards is once per card. The builder asks the same question ahead of them, so a teacher who
  // is holding the phone upright is told once, and by the screen they are actually looking at.
  if (state?.windowWidth && state?.windowHeight && state.windowWidth < state.windowHeight) {
    return (
      <div className="aq-shell aq-centered">
        <h2>Turn your phone sideways</h2>
        <p className="aq-notice">
          Questions are built the way a child plays them, and that needs the screen the wide way
          round. Rotate the device to carry on. (If nothing happens, check that auto-rotate is on.)
        </p>
        <button type="button" className="aq-btn" onClick={close}>Close</button>
      </div>
    );
  }

  if (launchError) {
    return (
      <div className="aq-shell aq-centered">
        <p>{launchError}</p>
        <button type="button" className="aq-btn" onClick={close}>Close</button>
      </div>
    );
  }

  if (!currentUser || loadingPlan) {
    return (
      <div className="aq-shell">
        <PortalLoader label="Loading your classroom…" />
      </div>
    );
  }

  return (
    <div className="aq-shell">
      <header className="aq-head">
        <div>
          <h1>
            {readOnly
              ? viewQuiz?.name || 'Quiz'
              : isEdit
                ? 'Edit quiz'
                : isAuto
                  ? 'Create an auto quiz'
                  : 'Create a quiz'}
          </h1>
          <p>
            {readOnly
              ? `${(viewQuiz?.questions || []).length} question${
                  (viewQuiz?.questions || []).length === 1 ? '' : 's'
                } · ${viewQuiz?.total_points ?? 0} point${(viewQuiz?.total_points ?? 0) === 1 ? '' : 's'}${
                  viewQuiz?.time_given ? ` · ${viewQuiz.time_given} min` : ' · no time limit'
                }`
              : isEdit
                ? `${viewQuiz?.name || 'This quiz'} - add or remove questions, then save.`
                : isAuto
                  ? 'Choose the module, sub-topics and number of questions. You can change or remove anything before you save the quiz.'
                  : 'Choose the questions for this quiz. Students get exactly what you see here.'}
          </p>
        </div>
        {/* The answers, for the teacher who has to mark this or teach from it. What is shown is each
            question's own worked explanation - the same one a student reads after answering - so it
            covers every kind of question, including the ones whose answer is a drawing or a position
            rather than a number. Off by default: a teacher looking at the class's quiz wants to see
            what the class will see. */}
        <Toggle
          label="Answers"
          checked={showAnswers}
          onChange={setShowAnswers}
          onDark
          title="Show each question's worked answer"
        />
        {/* The way out of a full-screen tool: the mark itself, as teacher-web's own overlays use. */}
        <button type="button" className="aq-close" onClick={close} title="Close" aria-label="Close">
          <FAClose color="#fff" />
        </button>
      </header>

      {isAuto && !readOnly ? (
        <section className="aq-pickers aq-auto-pickers">
          <select
            value={moduleId}
            aria-label="Module"
            onChange={(e) => {
              setModuleId(e.target.value);
              setAutoSubTopicIds([]);
              setNotice('');
            }}
          >
            <option value="">Select module</option>
            {modules.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>

          {/* Sub-topics, as many as they like: every skill of the ones ticked goes to the engine. */}
          <div className="aq-auto-topics" role="group" aria-label="Sub-topics">
            {(currentModule?.subTopics || []).length === 0 ? (
              <span className="aq-notice">Pick a module to see its sub-topics.</span>
            ) : (
              (currentModule?.subTopics || []).map((t) => (
                <label key={t.id} className="aq-check">
                  <input
                    type="checkbox"
                    checked={autoSubTopicIds.indexOf(t.id) !== -1}
                    onChange={(e) =>
                      setAutoSubTopicIds(
                        e.target.checked
                          ? [...autoSubTopicIds, t.id]
                          : autoSubTopicIds.filter((id) => id !== t.id),
                      )
                    }
                  />
                  <span>{t.name}</span>
                </label>
              ))
            )}
          </div>

          <label className="aq-field aq-field-inline">
            <span>Questions</span>
            <input
              type="number"
              min={1}
              max={MAX_PICKS}
              value={autoMax}
              onChange={(e) => setAutoMax(e.target.value)}
            />
          </label>

          {/* Only when this grade has any for the chosen sub-topics - the same rule the skill list
              uses before it offers HOTS at all. */}
          {autoHasHots ? (
            <label className="aq-check">
              <input
                type="checkbox"
                checked={autoHots}
                onChange={(e) => setAutoHots(e.target.checked)}
              />
              <span>Include HOTS</span>
            </label>
          ) : null}

          <button
            type="button"
            className="aq-btn aq-btn-primary"
            disabled={generating || autoSubTopicIds.length === 0 || !autoMaxIsValid}
            onClick={() => void generateQuiz()}
          >
            {generating ? 'Creating…' : picked.length > 0 ? 'Regenerate quiz' : 'Create quiz'}
          </button>
        </section>
      ) : !readOnly ? (
        <section className="aq-pickers">
          <select
            value={moduleId}
            aria-label="Module"
            onChange={(e) => {
              abandon();
              setModuleId(e.target.value);
              setSubTopicId('');
              setSkillId('');
              setSkills([]);
              setOffered([]);
              setPage(0);
              setLoadingQuestions(false);
              setNotice('');
            }}
          >
            <option value="">Select module</option>
            {modules.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>

          <select
            value={subTopicId}
            aria-label="Sub-topic"
            disabled={!currentModule}
            onChange={(e) => {
              abandon();
              setSubTopicId(e.target.value);
              setSkillId('');
              setSkills([]);
              setOffered([]);
              setPage(0);
              setLoadingQuestions(false);
              setNotice('');
            }}
          >
            <option value="">Select sub-topic</option>
            {(currentModule?.subTopics || []).map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>

          <select
            value={skillId}
            aria-label="Skill"
            disabled={!currentSubTopic || loadingSkills}
            onChange={(e) => onSelectSkill(e.target.value)}
          >
            <option value="">{loadingSkills ? 'Loading skills…' : 'Select skill'}</option>
            {skills.map((skill) => (
              <option key={skill.loId} value={skill.loId}>{skill.name}</option>
            ))}
            {/* HOTS last, and only when this sub-topic actually has any for the grade. It is not a
                skill like the others - it draws on the whole sub-topic - so it is set apart under
                its own heading and picked out in colour, where a teacher will find it. */}
            {hasHots ? (
              <optgroup label="Higher order thinking">
                <option className="aq-hots-option" value={HOTS_OPTION}>
                  HOTS
                </option>
              </optgroup>
            ) : null}
          </select>
        </section>
      ) : null}

      {/* Pinned ABOVE the body, not inside the questions column. In that column it inherited the
          centring an empty column uses, so it sat in the middle of the screen until the questions
          arrived and then jumped to the top. The quiz being full is about the whole screen, so it
          belongs across the top of it, in one place, whatever is below. */}
      {/* NOT in a read-only view, where nothing can be changed and the message offers something
          the teacher cannot do. And not while BUILDING an auto quiz: there is no list of questions
          to move from there - the engine fills the quiz directly - so "Move this one across" names
          a button that is not on screen. Editing still shows it, because its wording does not point
          at any control. */}
      {/* Across the top, with the other warning, not inside the questions column: that column
          centres whatever it holds while it is empty, so the message landed in the middle of the
          screen away from the box it is about. */}
      {isAuto && !readOnly && !autoMaxIsValid ? (
        <p className="aq-warn aq-warn-top">
          Enter how many questions you want, from 1 to {MAX_PICKS}.
        </p>
      ) : null}

      {quizIsFull && !readOnly && (isEdit || !isAuto) ? (
        <p className="aq-warn aq-warn-top">
          {isEdit
            ? `This quiz already has ${MAX_PICKS} questions. You can change the questions, but you cannot add more questions.`
            : `This quiz is full - ${MAX_PICKS} questions is the most it can hold. To bring a different one in, remove a question from the selected questions first, then Move this one across.`}
        </p>
      ) : null}

      <div className={`aq-body${widePanel ? ' aq-body-wide-panel' : ''}`}>
        {/* With no cards in it, this column holds nothing but what it has to say - so it says it in
            the middle, rather than in one line pinned under the pickers. */}
        <section className={`aq-questions${pageQuestions.length === 0 ? ' aq-questions-empty' : ''}`}>
          {notice ? <p className="aq-notice">{notice}</p> : null}
          {/* Auto quiz has no list to choose from: what the engine builds goes straight into the
              quiz, where it can be looked at, re-pointed or thrown out. */}
          {isAuto && !readOnly ? (
            generating ? (
              <p className="aq-inline-loading">
                <span className="aq-spinner aq-spinner-sm" role="status" aria-label="Creating the quiz" />
                Creating the quiz…
              </p>
            ) : picked.length > 0 ? (
              // No "on the right" anywhere in here: the app lays the selected questions out BELOW
              // rather than beside, and one wording has to serve both. Nothing names a direction.
              <p className="aq-notice">
                {picked.length} {picked.length === 1 ? 'question is' : 'questions are'} ready! Review
                the selected questions to see how they’ll appear to students. You can remove any
                questions you don’t want to include.
              </p>
            ) : modules.length === 0 ? (
              // Nothing to choose from, and the notice above says why. Asking them to choose a
              // module would contradict it.
              null
            ) : !moduleId ? (
              <p className="aq-notice">Choose a module to start.</p>
            ) : (
              <>
                <p className="aq-notice">
                  Tick the sub-topics to draw from, say how many questions you want, then create.
                </p>
                {/* Only once there are sub-topics to say it about, and only about the choice on
                    screen: what HOTS would add, or why it is not offered. */}
                {autoSubTopicIds.length > 0 ? (
                  <p className="aq-notice">
                    {autoHasHots ? (
                      <>
                        <strong>Include HOTS</strong> adds harder questions from across the
                        sub-topic. Leave it off to test the skills alone.
                      </>
                    ) : (
                      'These sub-topics have no HOTS for this grade.'
                    )}
                  </p>
                ) : null}
              </>
            )
          ) : null}
          {loadingQuestions ? (
            <p className="aq-inline-loading">
              <span className="aq-spinner aq-spinner-sm" role="status" aria-label="Loading questions" />
              Loading questions…
            </p>
          ) : null}

          {/* Nothing left to choose from here. Something has to be said, or the middle of the
              screen simply goes blank and reads as a fault rather than as an answer. A skill with
              no questions at all already says so in the notice above, so it is not repeated. */}
          {!isAuto && !readOnly && !loadingQuestions && available.length === 0 && emptyMessage ? (
            <p className="aq-notice">{emptyMessage}</p>
          ) : null}

          {!loadingQuestions && pageQuestions.length > 0 ? (
            <div className="aq-list-head">
              {readOnly ? (
                <span />
              ) : (
                <label className="aq-check">
                  <input type="checkbox" checked={allOnPageChecked} onChange={toggleAllOnPage} />
                  <span>Select all on this page</span>
                </label>
              )}
              <span className="aq-list-actions">
                {/* Ticking only marks a question. This is what puts it in the quiz - and takes it
                    out of the list, so the teacher always sees what is left to choose from. It sits
                    at this end, beside the list's own scrollbar, where the questions are. */}
                {!readOnly ? (
                  <button
                    type="button"
                    className="aq-btn aq-btn-primary"
                    disabled={selectedOnPage === 0}
                    onClick={moveSelected}
                  >
                    Move{selectedOnPage > 0 ? ` (${selectedOnPage})` : ''}
                  </button>
                ) : null}
              {pageCount > 1 ? (
                <span className="aq-pager">
                  <button type="button" className="aq-btn" disabled={page === 0} onClick={() => setPage(page - 1)}>
                    Prev
                  </button>
                  <span>
                    {page + 1} / {pageCount}
                  </span>
                  <button
                    type="button"
                    className="aq-btn"
                    disabled={page >= pageCount - 1}
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </button>
                </span>
              ) : null}
              </span>
            </div>
          ) : null}

          {!loadingQuestions &&
            pageQuestions.map((question, index) => (
              <QuestionCard
                key={`${questionKey(question)}-${index}`}
                response={question.response}
                index={page * PAGE_SIZE + index}
                readOnly={readOnly}
                checked={readOnly ? true : isSelected(question)}
                onToggle={() => toggleSelected(question)}
                blooms={question.blooms}
                points={readOnly ? question.points ?? 1 : pointsFor(question)}
                onRefresh={
                  !readOnly && question.type === 'AdaptivePractice'
                    ? () => void refreshQuestion(question)
                    : undefined
                }
                showAnswer={showAnswers}
                onExpand={() => setExpandedKey(questionKey(question))}
                expanded={expandedKey === questionKey(question)}
                onCollapse={() => setExpandedKey('')}
                refreshDisabled={refreshing === questionKey(question)}
              />
            ))}

          {!loadingQuestions && pageCount > 1 && pageQuestions.length > 0 ? (
            <div className="aq-pager aq-pager-bottom">
              <button type="button" className="aq-btn" disabled={page === 0} onClick={() => setPage(page - 1)}>
                Prev
              </button>
              <span>
                {page + 1} / {pageCount}
              </span>
              <button
                type="button"
                className="aq-btn"
                disabled={page >= pageCount - 1}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </div>
          ) : null}
        </section>

        {!readOnly ? (
          <SelectedQuestions
            picked={picked}
            totalPoints={totalPoints}
            onPoints={setPickedPoints}
            onView={setExpandedKey}
            onRemove={removePicked}
            onRemoveAll={() => setClearOpen(true)}
            onResetPoints={() => setPicked(picked.map((p) => ({ ...p, points: pointsForBlooms(p.blooms) })))}
            onSave={() => setSaveOpen(true)}
            isEdit={isEdit}
            onReorder={reorderPicked}
            wide={widePanel}
            onToggleWide={() => setWidePanel((on) => !on)}
          />
        ) : null}
      </div>

      {/* A question still in the list expands inside its own card. One that has been moved into the
          quiz has no card any more, so the eye in the Selected list mounts its frame here. */}
      {expandedQuestion && !availableKeys.includes(expandedKey) ? (
        <QuestionFrame
          response={expandedQuestion.response}
          showAnswer={showAnswers}
          expanded
          onCollapse={() => setExpandedKey('')}
          onRefresh={
            !readOnly && expandedQuestion.type === 'AdaptivePractice'
              ? () => void refreshQuestion(expandedQuestion)
              : undefined
          }
          refreshing={refreshing === expandedKey}
        />
      ) : null}

      {/* Emptying the quiz: asked in front of everything else, worded as what it does, and with the
          way out under the cursor's own hand. Nothing is written until the quiz is saved, which the
          dialog says, so the teacher can see how far back this puts them. */}
      {clearOpen ? (
        <div className="aq-modal-bg">
          <div className="aq-modal aq-modal-danger" role="alertdialog" aria-modal="true" aria-labelledby="aq-clear-title">
            <h2 id="aq-clear-title">Remove all questions?</h2>
            <p>
              All {picked.length} question{picked.length === 1 ? '' : 's'} will be taken out of this
              quiz. You can choose them again, and nothing changes for students until you save.
            </p>
            <div className="aq-modal-actions">
              <button type="button" className="aq-btn" onClick={() => setClearOpen(false)} autoFocus>
                Keep them
              </button>
              <button
                type="button"
                className="aq-btn aq-btn-remove"
                onClick={() => {
                  setPicked([]);
                  setNotice('');
                  setClearOpen(false);
                }}
              >
                Remove all
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {saveOpen ? (
        <SaveQuizDialog
          questionCount={picked.length}
          totalPoints={totalPoints}
          onCancel={() => setSaveOpen(false)}
          quiz={isEdit ? viewQuiz : undefined}
          onSave={async ({ time_given, ...details }) => {
            // No limit is sent by leaving the field out: the API takes minutes of 1 or more, and
            // an update with no time clears whatever limit the quiz had.
            const timing = time_given ? { time_given } : {};
            const questions = picked.map((p) => ({
              type: p.type,
              question_id: p.question_id,
              ...(p.lo_id ? { lo_id: p.lo_id } : {}),
              ...(p.review_obj ? { review_obj: p.review_obj } : {}),
              points: p.points,
            }));
            // What a quiz IS - the classroom it was built for, its grade, whether it was built
            // automatically - does not change when it is edited, so only the rest is sent.
            const res: any = isEdit
              ? await apis._updateAdaptiveQuiz({ quiz_id: quizId, ...details, ...timing, questions })
              : await apis._createAdaptiveQuiz({
                  classroom_id: classroomId,
                  curriculum_id: curriculumId,
                  grade,
                  isAuto,
                  ...details,
                  ...timing,
                  questions,
                });
            if (res?.data?.code !== 200) throw new Error(res?.data?.message || 'Could not save the quiz.');
            close();
          }}
        />
      ) : null}
    </div>
  );
}

export default AdaptiveQuizBuilder;
